import { useState, useEffect, useRef } from 'react';

interface ImposterAnimationState {
  isAnimating: boolean;
  animationPhase: 'flash' | 'pulse' | 'settle' | 'complete';
  showWarning: boolean;
  cardClasses: string;
}

/**
 * Custom hook to manage imposter detection animation
 * Triggers animation when isPossibleImposter changes from false to true
 */
export const useImposterAnimation = (
  applicantId: string,
  isPossibleImposter: boolean
): ImposterAnimationState => {
  const [animationState, setAnimationState] = useState<ImposterAnimationState>({
    isAnimating: false,
    animationPhase: 'complete',
    showWarning: isPossibleImposter || false,
    cardClasses: ''
  });

  // Track previous state to detect false→true transitions
  const previousImposterState = useRef<boolean | undefined>(undefined);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);

  // Cleanup function to clear all timeouts
  const clearAllTimeouts = () => {
    timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = [];
  };

  useEffect(() => {
    // Only trigger animation on false→true transition
    const shouldAnimate = 
      previousImposterState.current === false && 
      isPossibleImposter === true;

    if (shouldAnimate) {
      console.log(`Triggering imposter animation for applicant ${applicantId}`);
      
      // Clear any existing timeouts
      clearAllTimeouts();

      // Start animation sequence
      setAnimationState({
        isAnimating: true,
        animationPhase: 'flash',
        showWarning: true,
        cardClasses: 'animate-imposter-flash'
      });

      // Flash phase (500ms)
      const flashTimeout = setTimeout(() => {
        setAnimationState(prev => ({
          ...prev,
          animationPhase: 'pulse',
          cardClasses: 'animate-imposter-pulse'
        }));
      }, 500);
      timeoutRefs.current.push(flashTimeout);

      // Pulse phase (2400ms - 3 pulses × 800ms each)
      const pulseTimeout = setTimeout(() => {
        setAnimationState(prev => ({
          ...prev,
          animationPhase: 'settle',
          cardClasses: 'animate-imposter-settle'
        }));
      }, 500 + 2400);
      timeoutRefs.current.push(pulseTimeout);

      // Settle phase (1000ms fade)
      const settleTimeout = setTimeout(() => {
        setAnimationState(prev => ({
          ...prev,
          isAnimating: false,
          animationPhase: 'complete',
          cardClasses: ''
        }));
      }, 500 + 2400 + 1000);
      timeoutRefs.current.push(settleTimeout);

    } else if (isPossibleImposter !== previousImposterState.current) {
      // Handle other state changes (true→false, initial load)
      setAnimationState(prev => ({
        ...prev,
        showWarning: isPossibleImposter || false,
        isAnimating: false,
        animationPhase: 'complete',
        cardClasses: ''
      }));
    }

    // Update previous state
    previousImposterState.current = isPossibleImposter;
  }, [applicantId, isPossibleImposter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  return animationState;
};
